import { useState, useRef, useCallback, useEffect } from "react";
import { cn, getColorById, formatTime, timeToMinutes, minutesToTime, doTimesOverlap } from "../lib/utils";
import { toast } from "sonner";
import { Coffee, Utensils, Clock } from "lucide-react";
import ShiftContextMenu from "./ShiftContextMenu";

// Icon mapping for breaks
const BREAK_ICON_MAP = {
  coffee: Coffee,
  utensils: Utensils,
  clock: Clock,
};

const getBreakIcon = (iconName) => BREAK_ICON_MAP[iconName] || Coffee;

const ShiftBlock = ({ 
  shift, 
  employee, 
  employeeShifts = [], // All shifts for this employee on this date
  breakTypes = [], // Break type definitions
  timelineStart, 
  timelineEnd, 
  onUpdate, 
  onClick,
  onContextEdit,
  onContextCopy,
  onContextDelete,
  snapInterval = 15,
  shiftGapMinutes = 0,
  scrollable = false,
  isLocked = false, // Whether this shift is on a locked (past) date
  onDragGuideUpdate,
}) => {
  const [optimisticTimes, setOptimisticTimes] = useState(null);
  const [optimisticSegments, setOptimisticSegments] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasOverlap, setHasOverlap] = useState(false);
  const blockRef = useRef(null);
  const dragDataRef = useRef(null);
  const lastDragTimeRef = useRef(0);

  const color = getColorById(employee?.color);
  
  // Use optimistic times if set, otherwise use shift props
  const displayStart = optimisticTimes?.start_time || shift.start_time;
  const displayEnd = optimisticTimes?.end_time || shift.end_time;
  const displaySegments = optimisticSegments || shift.rate_segments || [];
  
  // Check if this is a split shift
  const isSplitShift = displaySegments.length > 0;
  
  // Clear optimistic state when shift props match what we set
  useEffect(() => {
    if (optimisticTimes) {
      if (shift.start_time === optimisticTimes.start_time && 
          shift.end_time === optimisticTimes.end_time) {
        setOptimisticTimes(null);
        setHasOverlap(false);
      }
    }
    if (optimisticSegments) {
      // Simple check - if segments count matches, clear optimistic
      if (JSON.stringify(shift.rate_segments) === JSON.stringify(optimisticSegments)) {
        setOptimisticSegments(null);
      }
    }
  }, [shift.start_time, shift.end_time, shift.rate_segments, optimisticTimes, optimisticSegments]);
  
  // Calculate position and width
  const timelineStartMinutes = timeToMinutes(timelineStart);
  const timelineEndMinutes = timeToMinutes(timelineEnd);
  const totalMinutes = timelineEndMinutes - timelineStartMinutes;
  
  const startMinutes = timeToMinutes(displayStart);
  const endMinutes = timeToMinutes(displayEnd);
  
  const leftPercent = ((startMinutes - timelineStartMinutes) / totalMinutes) * 100;
  const widthPercent = ((endMinutes - startMinutes) / totalMinutes) * 100;
  
  // Add small visual gap between shifts (1px on each side = 2px total gap)
  const gapPixels = 1;

  // Snap to interval
  const snapToInterval = useCallback((minutes) => {
    return Math.round(minutes / snapInterval) * snapInterval;
  }, [snapInterval]);

  // Check if proposed times overlap with other shifts (with gap)
  const checkOverlap = useCallback((newStart, newEnd) => {
    const otherShifts = employeeShifts.filter(s => s.id !== shift.id);
    
    for (const other of otherShifts) {
      if (doTimesOverlap(newStart, newEnd, other.start_time, other.end_time, shiftGapMinutes)) {
        return true;
      }
    }
    return false;
  }, [employeeShifts, shift.id, shiftGapMinutes]);

  // Handle main shift drag (move or resize outer edges)
  const handleMouseDown = useCallback((e, type) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Don't allow drag/resize on locked shifts
    if (isLocked) return;
    
    const rect = blockRef.current?.parentElement?.getBoundingClientRect();
    if (!rect) return;

    dragDataRef.current = {
      startX: e.clientX,
      originalStart: timeToMinutes(displayStart),
      originalEnd: timeToMinutes(displayEnd),
      originalSegments: displaySegments,
      containerWidth: rect.width,
      type,
      hasMoved: false,
      finalTimes: null,
      finalSegments: null,
    };

    setIsDragging(true);

    const handleMouseMove = (moveEvent) => {
      if (!dragDataRef.current) return;
      
      const { startX, originalStart, originalEnd, originalSegments, containerWidth, type } = dragDataRef.current;
      const deltaX = moveEvent.clientX - startX;
      
      // Only consider it a drag if moved more than 3 pixels
      if (Math.abs(deltaX) > 3) {
        dragDataRef.current.hasMoved = true;
      }
      
      if (!dragDataRef.current.hasMoved) return;
      
      const rawDeltaMinutes = (deltaX / containerWidth) * totalMinutes;

      let newStart = originalStart;
      let newEnd = originalEnd;
      let newSegments = originalSegments;

      if (type === 'move') {
        const deltaMinutes = snapToInterval(rawDeltaMinutes);
        newStart = originalStart + deltaMinutes;
        newEnd = originalEnd + deltaMinutes;
        
        const duration = newEnd - newStart;
        if (newStart < timelineStartMinutes) {
          newStart = timelineStartMinutes;
          newEnd = newStart + duration;
        }
        if (newEnd > timelineEndMinutes) {
          newEnd = timelineEndMinutes;
          newStart = newEnd - duration;
        }
        
        // Move all segments by the same delta (keep fixed times relative to shift)
        if (originalSegments.length > 0) {
          const actualDelta = newStart - originalStart;
          newSegments = originalSegments.map(seg => ({
            ...seg,
            start_time: minutesToTime(timeToMinutes(seg.start_time) + actualDelta),
            end_time: minutesToTime(timeToMinutes(seg.end_time) + actualDelta),
          }));
        }
      } else if (type === 'left') {
        newStart = snapToInterval(originalStart + rawDeltaMinutes);
        newStart = Math.max(timelineStartMinutes, Math.min(originalEnd - snapInterval, newStart));
        
        // Adjust first segment if split shift
        if (originalSegments.length > 0) {
          newSegments = [...originalSegments];
          newSegments[0] = { ...newSegments[0], start_time: minutesToTime(newStart) };
        }
      } else if (type === 'right') {
        newEnd = snapToInterval(originalEnd + rawDeltaMinutes);
        newEnd = Math.min(timelineEndMinutes, Math.max(originalStart + snapInterval, newEnd));
        
        // Adjust last segment if split shift
        if (originalSegments.length > 0) {
          newSegments = [...originalSegments];
          newSegments[newSegments.length - 1] = { 
            ...newSegments[newSegments.length - 1], 
            end_time: minutesToTime(newEnd) 
          };
        }
      }

      const times = {
        start_time: minutesToTime(newStart),
        end_time: minutesToTime(newEnd),
      };
      
      // Check for overlap during drag
      const wouldOverlap = checkOverlap(times.start_time, times.end_time);
      setHasOverlap(wouldOverlap);
      
      dragDataRef.current.finalTimes = times;
      dragDataRef.current.finalSegments = newSegments;
      setOptimisticTimes(times);
      if (newSegments !== originalSegments) {
        setOptimisticSegments(newSegments);
      }
      
      // Update drag guides
      if (onDragGuideUpdate) {
        const startPercent = ((newStart - timelineStartMinutes) / totalMinutes) * 100;
        const endPercent = ((newEnd - timelineStartMinutes) / totalMinutes) * 100;
        onDragGuideUpdate({ startPercent, endPercent });
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      const data = dragDataRef.current;
      const didMove = data?.hasMoved;
      
      setIsDragging(false);
      
      // Clear drag guides
      if (onDragGuideUpdate) {
        onDragGuideUpdate(null);
      }
      
      if (didMove && data?.finalTimes) {
        lastDragTimeRef.current = Date.now();
        
        const originalStartTime = minutesToTime(data.originalStart);
        const originalEndTime = minutesToTime(data.originalEnd);
        
        if (data.finalTimes.start_time !== originalStartTime || data.finalTimes.end_time !== originalEndTime) {
          // Check for overlap before saving
          const wouldOverlap = checkOverlap(data.finalTimes.start_time, data.finalTimes.end_time);
          
          if (wouldOverlap) {
            const gapText = shiftGapMinutes > 0 
              ? `Shifts must be at least ${shiftGapMinutes} minutes apart`
              : "Cannot overlap with existing shift";
            toast.error(gapText);
            // Reset to original position
            setOptimisticTimes(null);
            setOptimisticSegments(null);
            setHasOverlap(false);
          } else {
            const updateData = { ...data.finalTimes };
            if (data.finalSegments && data.finalSegments.length > 0) {
              updateData.rate_segments = data.finalSegments;
            }
            onUpdate(updateData);
          }
        } else {
          setOptimisticTimes(null);
          setOptimisticSegments(null);
        }
      } else {
        setOptimisticTimes(null);
        setOptimisticSegments(null);
      }
      
      setHasOverlap(false);
      dragDataRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [displayStart, displayEnd, displaySegments, onUpdate, totalMinutes, timelineStartMinutes, timelineEndMinutes, snapToInterval, snapInterval, checkOverlap, shiftGapMinutes, onDragGuideUpdate, isLocked]);

  // Handle internal split point drag
  const handleSplitDrag = useCallback((e, segmentIndex) => {
    e.stopPropagation();
    e.preventDefault();
    
    const rect = blockRef.current?.parentElement?.getBoundingClientRect();
    if (!rect) return;

    const originalSegments = [...displaySegments];
    const segmentToAdjust = originalSegments[segmentIndex];
    const nextSegment = originalSegments[segmentIndex + 1];
    
    if (!nextSegment) return; // Can't drag the last segment's end
    
    const originalSplitTime = timeToMinutes(segmentToAdjust.end_time);
    const minSplitTime = timeToMinutes(segmentToAdjust.start_time) + snapInterval;
    const maxSplitTime = timeToMinutes(nextSegment.end_time) - snapInterval;

    dragDataRef.current = {
      startX: e.clientX,
      originalSplitTime,
      minSplitTime,
      maxSplitTime,
      segmentIndex,
      originalSegments,
      containerWidth: rect.width,
      type: 'split',
      hasMoved: false,
    };

    setIsDragging(true);

    const handleMouseMove = (moveEvent) => {
      if (!dragDataRef.current || dragDataRef.current.type !== 'split') return;
      
      const { startX, originalSplitTime, minSplitTime, maxSplitTime, segmentIndex, originalSegments, containerWidth } = dragDataRef.current;
      const deltaX = moveEvent.clientX - startX;
      
      if (Math.abs(deltaX) > 3) {
        dragDataRef.current.hasMoved = true;
      }
      
      if (!dragDataRef.current.hasMoved) return;
      
      const rawDeltaMinutes = (deltaX / containerWidth) * totalMinutes;
      let newSplitTime = snapToInterval(originalSplitTime + rawDeltaMinutes);
      newSplitTime = Math.max(minSplitTime, Math.min(maxSplitTime, newSplitTime));
      
      const newSplitTimeStr = minutesToTime(newSplitTime);
      
      const newSegments = originalSegments.map((seg, i) => {
        if (i === segmentIndex) {
          return { ...seg, end_time: newSplitTimeStr };
        }
        if (i === segmentIndex + 1) {
          return { ...seg, start_time: newSplitTimeStr };
        }
        return seg;
      });
      
      dragDataRef.current.finalSegments = newSegments;
      setOptimisticSegments(newSegments);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      const data = dragDataRef.current;
      const didMove = data?.hasMoved;
      
      setIsDragging(false);
      
      if (didMove && data?.finalSegments) {
        lastDragTimeRef.current = Date.now();
        onUpdate({ rate_segments: data.finalSegments });
      } else {
        setOptimisticSegments(null);
      }
      
      dragDataRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [displaySegments, onUpdate, totalMinutes, snapToInterval, snapInterval]);

  const handleClick = (e) => {
    e.stopPropagation();
    
    const timeSinceLastDrag = Date.now() - lastDragTimeRef.current;
    if (timeSinceLastDrag < 500) {
      return;
    }
    
    if (dragDataRef.current?.hasMoved) {
      return;
    }
    
    // Still allow clicking to view shift details (read-only) when locked
    onClick?.(shift);
  };

  // Calculate segment positions within the shift block
  const getSegmentStyle = (segment, index) => {
    const shiftStartMins = timeToMinutes(displayStart);
    const shiftEndMins = timeToMinutes(displayEnd);
    const shiftDuration = shiftEndMins - shiftStartMins;
    
    const segStartMins = timeToMinutes(segment.start_time);
    const segEndMins = timeToMinutes(segment.end_time);
    
    const segLeftPercent = ((segStartMins - shiftStartMins) / shiftDuration) * 100;
    const segWidthPercent = ((segEndMins - segStartMins) / shiftDuration) * 100;
    
    return {
      left: `${segLeftPercent}%`,
      width: `${segWidthPercent}%`,
    };
  };

  return (
    <ShiftContextMenu
      shift={shift}
      employee={employee}
      onEdit={onContextEdit}
      onCopy={onContextCopy}
      onDelete={onContextDelete}
      isLocked={isLocked}
    >
      <div
        ref={blockRef}
        className={cn(
          "shift-block absolute top-1 bottom-1 rounded-md border select-none",
          "flex items-center justify-center overflow-hidden transition-shadow",
          color.bg,
          color.border,
          isDragging && "opacity-90 shadow-lg z-20",
          !isDragging && !isLocked && "cursor-grab hover:shadow-md",
          !isDragging && isLocked && "cursor-default opacity-75",
          hasOverlap && "ring-2 ring-red-500 border-red-500"
        )}
        style={{
          left: `calc(${leftPercent}% + ${gapPixels}px)`,
          width: `calc(${widthPercent}% - ${gapPixels * 2}px)`,
          minWidth: '40px',
        }}
        data-testid={`shift-block-${shift.id}`}
        onClick={handleClick}
        onMouseDown={(e) => handleMouseDown(e, 'move')}
      >
      {/* Rate segments visualization for split shifts */}
      {isSplitShift && displaySegments.map((segment, index) => {
        const isNonDefault = segment.rate_name !== null;
        const segStyle = getSegmentStyle(segment, index);
        const isLastSegment = index === displaySegments.length - 1;
        
        return (
          <div key={index} className="absolute inset-y-0 pointer-events-none" style={segStyle}>
            {/* Very subtle overlay for non-default rate segments */}
            {isNonDefault && (
              <div className="absolute inset-0 bg-black/[0.04] dark:bg-black/[0.08]" />
            )}
            
            {/* Rate name tag at bottom - uses employee color */}
            {isNonDefault && (
              <div 
                className="absolute bottom-0 left-1 right-1 h-3 flex items-center justify-center pointer-events-none"
                title={`${segment.rate_name}: ${formatTime(segment.start_time)} - ${formatTime(segment.end_time)}`}
              >
                <span className={cn(
                  "text-[8px] font-medium px-1 rounded-t-sm truncate max-w-full opacity-50",
                  color.bar,
                  "text-white"
                )}>
                  {segment.rate_name}
                </span>
              </div>
            )}
            
            {/* Internal split drag handle (between segments) */}
            {!isLastSegment && (
              <div
                className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-white/30 dark:hover:bg-black/30 z-20 pointer-events-auto"
                style={{ transform: 'translateX(50%)' }}
                onMouseDown={(e) => handleSplitDrag(e, index)}
                onClick={(e) => e.stopPropagation()}
                title="Drag to adjust split time"
              >
                <div className="absolute inset-y-2 left-1/2 w-0.5 bg-white/50 dark:bg-black/30 -translate-x-1/2" />
              </div>
            )}
          </div>
        );
      })}

      {/* Break tabs - small indicators at the bottom */}
      {shift.breaks?.map((brk, index) => {
        const breakType = breakTypes.find(bt => bt.id === brk.break_type_id);
        if (!breakType) return null;
        
        const shiftStartMins = timeToMinutes(displayStart);
        const shiftEndMins = timeToMinutes(displayEnd);
        const shiftDuration = shiftEndMins - shiftStartMins;
        
        const breakStartMins = timeToMinutes(brk.start_time);
        const breakEndMins = timeToMinutes(brk.end_time);
        
        // Calculate position within the shift block
        const breakLeftPercent = ((breakStartMins - shiftStartMins) / shiftDuration) * 100;
        const breakWidthPercent = ((breakEndMins - breakStartMins) / shiftDuration) * 100;
        
        const BreakIcon = getBreakIcon(breakType.icon);
        
        return (
          <div
            key={`break-${index}`}
            className={cn(
              "absolute bottom-0 h-3 flex items-center justify-center pointer-events-none rounded-t-sm z-10 opacity-50",
              color.bar
            )}
            style={{
              left: `${breakLeftPercent}%`,
              width: `${breakWidthPercent}%`,
              minWidth: '16px',
            }}
            title={`${breakType.name}: ${formatTime(brk.start_time)} - ${formatTime(brk.end_time)}`}
          >
            <BreakIcon className="w-2.5 h-2.5 text-white" />
          </div>
        );
      })}

      {/* Single rate indicator - shown only for non-split shifts with non-default rate */}
      {!isSplitShift && shift.rate_name && (
        <div
          className={cn(
            "absolute bottom-0 right-1 h-3 px-1.5 flex items-center gap-0.5 pointer-events-none rounded-t-sm z-10 opacity-50",
            color.bar
          )}
          title={`Using ${shift.rate_name} rate`}
        >
          <span className="text-[8px] font-medium text-white truncate max-w-[60px]">{shift.rate_name}</span>
        </div>
      )}
      
      {/* Left resize handle - invisible until hover, hidden when locked */}
      {!isLocked && (
        <div
          className="absolute left-0 top-0 bottom-0 w-4 cursor-ew-resize hover:bg-black/20 dark:hover:bg-white/20 rounded-l-md transition-colors z-10"
          onMouseDown={(e) => {
            e.stopPropagation();
            handleMouseDown(e, 'left');
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`shift-handle-left-${shift.id}`}
        />
      )}
      
      {/* Content - push up slightly if breaks exist or split shift */}
      <div className={cn(
        "flex-1 px-4 text-xs tabular-nums truncate text-center pointer-events-none z-10",
        color.text,
        hasOverlap && "text-red-600 dark:text-red-400",
        (shift.breaks?.length > 0 || isSplitShift) && "-mt-2"
      )}>
        {widthPercent > 8 ? (
          <span>{formatTime(displayStart)} - {formatTime(displayEnd)}</span>
        ) : (
          <span>{formatTime(displayStart)}</span>
        )}
      </div>
      
      {/* Right resize handle - invisible until hover, hidden when locked */}
      {!isLocked && (
        <div
          className="absolute right-0 top-0 bottom-0 w-4 cursor-ew-resize hover:bg-black/20 dark:hover:bg-white/20 rounded-r-md transition-colors z-10"
          onMouseDown={(e) => {
            e.stopPropagation();
            handleMouseDown(e, 'right');
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`shift-handle-right-${shift.id}`}
        />
      )}
      </div>
    </ShiftContextMenu>
  );
};

export default ShiftBlock;
